/* DoctorBilling — top-level billing dashboard across all patients.
 *
 * iter 98.29 — full rewrite based on the HTML mockup the user supplied.
 * Strict design rules applied:
 *   - NO left-rail accents on stat cards (icon chip top-right instead)
 *   - Centered Dialog modal for invoice detail (already InvoiceDetailDialog)
 *   - Consistent header style (font-display text-2xl, rounded-full sm buttons)
 *   - Teal/emerald palette only
 *
 * Two top-level lenses (segmented tab bar):
 *   "patient"   — invoices the clinic issued to its patients
 *   "invisique" — invoices Invisique HQ issued to this clinic (B2B)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import {
    Receipt, MagnifyingGlass, FunnelSimple, CurrencyInr, Buildings, FileText,
    DownloadSimple, Plus, Warning, Clock, TrendUp, PaperPlaneTilt,
    CalendarBlank, CaretRight, CaretLeft, CaretDown, ArrowRight, Money, Prohibit, Stack,
    ChartLineUp, X, Wallet,
} from "@phosphor-icons/react";
import InvoiceDetailDialog from "@/components/case/InvoiceDetailDialog";
import InvoiceFormDialog from "@/components/case/InvoiceFormDialog";
import RecordPaymentDialog from "@/components/case/RecordPaymentDialog";
import PickInvoiceForPaymentDialog from "@/components/case/PickInvoiceForPaymentDialog";
import AddPatientDialog from "@/components/doctor/AddPatientDialog";
import PatientAvatar from "@/components/patient/PatientAvatar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BillingExportDialog from "@/components/doctor/BillingExportDialog";


/* ─────────────── Constants & helpers ─────────────── */

/* iter100.194 — Two synthetic filter values (not real invoice statuses)
   that map the top KPI cards to a meaningful filtered view of the table.
   - `outstanding`   → any invoice with balance > 0 (across sent / viewed
                       / partial / overdue). Wired to the "Outstanding
                       A/R" KPI card click.
   - `paid_this_month` → invoices with at least one payment recorded in
                       the current calendar month. Wired to the
                       "Collected This Month" KPI card click.
   Kept out of `STATUS_CHIPS` so they don't appear as filter pills
   below — they're click-outcomes from the KPI strip only, and the
   presence of a search-bar clear button + the "All" chip both remain
   available to reset. */
const SYNTHETIC_STATUS = new Set(["outstanding", "paid_this_month"]);

const STATUS_CHIPS = [
    { id: "all",              label: "All" },
    // iter100.194 — Two synthetic aggregate chips added at the front
    // of the row so KPI-card clicks (Outstanding A/R + Collected This
    // Month) surface their filter state exactly where the doctor
    // expects it — inline with the other invoice-status filters.
    { id: "outstanding",      label: "Outstanding" },
    { id: "paid_this_month",  label: "Paid this month" },
    { id: "draft",            label: "Drafts" },
    { id: "sent",             label: "Sent" },
    { id: "viewed",           label: "Viewed" },
    { id: "partial",          label: "Partly Paid" },
    { id: "paid",             label: "Paid" },
    { id: "overdue",          label: "Overdue" },
    { id: "uncollectible",    label: "Uncollectible" },
    { id: "refunded",         label: "Refunded" },
    { id: "void",             label: "Void" },
];

const STATUS_TONE = {
    // Patient-invoice (Doctor → Patient) lifecycle (iter98.99x).
    draft:         { label: "Draft",         bg: "bg-foreground/5 text-foreground/80 border-foreground/15" },
    sent:          { label: "Sent",          bg: "bg-primary/10 text-primary border-primary/20" },
    viewed:        { label: "Viewed",        bg: "bg-sky-50 text-sky-700 border-sky-200" },
    partial:       { label: "Partly paid",   bg: "bg-amber-50 text-amber-800 border-amber-200" },
    paid:          { label: "Paid",          bg: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    refunded:      { label: "Refunded",      bg: "bg-violet-50 text-violet-700 border-violet-200" },
    uncollectible: { label: "Uncollectible", bg: "bg-slate-100 text-slate-700 border-slate-300" },
    void:          { label: "Void",          bg: "bg-rose-50 text-rose-700 border-rose-200 line-through" },
    overdue:       { label: "Overdue",       bg: "bg-rose-50 text-rose-700 border-rose-200" },
    // B2B-invoice (Invisique → Doctor) lifecycle — iter98.99e0.
    outstanding:    { label: "Unpaid",        bg: "bg-amber-50 text-amber-800 border-amber-200" },
    partially_paid: { label: "Partly paid",   bg: "bg-amber-100 text-amber-900 border-amber-300" },
    cancelled:      { label: "Cancelled",     bg: "bg-slate-100 text-slate-600 border-slate-200" },
};

/* Payment-method pill styling — iter102.33.
   Instead of a dedicated column (which would crush the layout on a
   6-col table already tight on width), we surface the payment
   method(s) as tiny pill chips directly under "Paid: ₹X" in the
   Amount cell. Color-coded so a doctor can eyeball the receipt mix
   without reading text: amber = Cash, teal = UPI, violet = Card,
   slate = Bank/Cheque/Other. When "Hide cash" is on the cash chip
   naturally disappears (its payments have been stripped upstream). */
const METHOD_PILL_STYLE = {
    cash:          { label: "Cash",   className: "bg-amber-50 text-amber-800 border-amber-200" },
    upi:           { label: "UPI",    className: "bg-primary/10 text-primary border-primary/25" },
    card:          { label: "Card",   className: "bg-violet-50 text-violet-700 border-violet-200" },
    bank_transfer: { label: "Bank",   className: "bg-slate-50 text-slate-700 border-slate-200" },
    cheque:        { label: "Cheque", className: "bg-slate-50 text-slate-700 border-slate-200" },
    other:         { label: "Other",  className: "bg-slate-50 text-slate-700 border-slate-200" },
};
// iter102.37 — Order used by the on-screen "Method" filter popover.
// Kept in sync with METHOD_PILL_STYLE keys so both the trigger badge
// and the invoice row chips speak the same visual language.
const METHOD_KEYS = ["cash", "upi", "card", "bank_transfer", "cheque", "other"];

function formatINR(amount) {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 0,
    }).format(amount);
}
function isInCurrentMonth(iso) {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}
function fmtDateShort(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function isOverdue(inv) {
    // iter98.99e0 — terminal/no-AR statuses no longer accumulate overdue.
    const TERMINAL = new Set(["paid", "void", "draft", "cancelled"]);
    if (!inv.due_at || !inv.balance || TERMINAL.has(inv.status)) return false;
    return new Date(inv.due_at) < new Date();
}

/* iter100.194 — Client-side CSV export helpers. Replaces the previous
   `window.print()` shortcut on the Export button which had no way to
   scope its output to just the billing table (it printed the entire
   viewport including sidebar + chrome).
   Kept tiny + dependency-free — Papaparse would be overkill for the
   ≤ several-hundred-row billing table. Escape logic follows RFC 4180:
   wrap fields in double-quotes only when they contain commas /
   newlines / quotes; escape embedded quotes by doubling. */
function csvEscape(v) {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(rows, filename) {
    if (!rows || rows.length === 0) {
        toast.info("Nothing to export in the current view");
        return;
    }
    const headers = Object.keys(rows[0]);
    const lines = [
        headers.map(csvEscape).join(","),
        ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
}
function todayStamp() {
    return new Date().toISOString().slice(0, 10);
}


/* ─────────────── Stat card (NO left-rail accent) ─────────────── */

/* Tone -> icon chip color. Stays consistent with Dashboard/Appointments
   stat strips. No left-rail decoration; tone communicated via the icon chip
   top-right + the colored value. */
const TONE = {
    rose:    { chip: "bg-rose-50 text-rose-700",       val: "text-rose-700" },
    amber:   { chip: "bg-amber-50 text-amber-700",     val: "text-amber-700" },
    emerald: { chip: "bg-emerald-50 text-emerald-700", val: "text-emerald-700" },
    sky:     { chip: "bg-sky-50 text-sky-700",         val: "text-sky-700" },
    violet:  { chip: "bg-violet-50 text-violet-700",   val: "text-violet-700" },
    primary: { chip: "bg-primary/10 text-primary",     val: "text-primary" },
    neutral: { chip: "bg-secondary text-foreground/70",val: "text-foreground" },
};

function StatCard({ label, value, sub, tone = "neutral", icon: Icon, subTone, testid, onClick }) {
    const t = TONE[tone] || TONE.neutral;
    const subClass = subTone === "danger"  ? "text-rose-700"
                   : subTone === "warn"    ? "text-amber-700"
                   : subTone === "success" ? "text-emerald-700"
                   : "text-muted-foreground";
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`relative bg-card border border-border rounded-2xl p-4 text-left transition-all ${
                onClick ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "cursor-default"
            }`}
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                {Icon && (
                    <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${t.chip}`}>
                        <Icon size={14} weight="duotone" />
                    </span>
                )}
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums leading-none ${t.val}`}>{value}</div>
            {sub && <div className={`text-[11px] mt-1.5 ${subClass}`}>{sub}</div>}
        </button>
    );
}


/* ─────────────── Main page ─────────────── */

export default function DoctorBilling() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") === "invisique" ? "invisique" : "patient";
    const [topTab, setTopTab] = useState(initialTab);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusChip, setStatusChip] = useState("all");
    // iter102.37 — On-screen "Payment method" filter. Set semantics: an
    // invoice matches if it has ≥1 payment whose method is in this set
    // OR the invoice has no payments (so unpaid drafts/sent invoices
    // still surface — the filter is about *payments*, not receivables).
    // When the set contains every method key we short-circuit the
    // filter entirely for perf + "All methods" labelling.
    const [methodFilter, setMethodFilter] = useState(() => new Set(METHOD_KEYS));
    const [query, setQuery] = useState("");
    const [openInvoiceId, setOpenInvoiceId] = useState(null);
    const [b2bCount, setB2bCount] = useState(0);
    const [b2bOutstanding, setB2bOutstanding] = useState(0);
    /* iter98.99w — In-page patient-invoice creation. Clicking "New
     * invoice" no longer kicks the doctor out to /doctor/patients; we
     * open a small patient picker right here, then the existing
     * InvoiceFormDialog scoped to the picked patient — same experience
     * as the patient profile's "New invoice" button. */
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerQuery, setPickerQuery] = useState("");
    const [pickerPatients, setPickerPatients] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [pickedPatient, setPickedPatient] = useState(null);
    const [pickedPatientCases, setPickedPatientCases] = useState([]);
    /* iter98.99z (2026-06) — Quick-pay state. The doctor can record a
     * payment in two clicks: either via the row's Money icon or via the
     * top-level "Record payment" CTA → invoice picker → quick pay. */
    const [quickPayInvoice, setQuickPayInvoice] = useState(null);
    const [showInvoicePicker, setShowInvoicePicker] = useState(false);
    /* iter98.99y — Inline "+ Create new patient" from the picker.
     * If the doctor types a name that doesn't match any patient, we
     * show a "+ Create [name]" affordance at the bottom of the list
     * that opens the existing AddPatientDialog. On success, the new
     * patient is auto-selected and the invoice form opens for them. */
    const [showAddPatient, setShowAddPatient] = useState(false);
    const [addPatientPrefill, setAddPatientPrefill] = useState("");

    const openPatientPicker = async () => {
        setPickerOpen(true);
        setPickerQuery("");
        // Lazy-load patients on first open so the picker is fast.
        if (pickerPatients.length === 0) {
            setPickerLoading(true);
            try {
                const r = await api.get("/doctor/patients");
                setPickerPatients(r.data || []);
            } catch {
                toast.error("Couldn't load patients");
            } finally {
                setPickerLoading(false);
            }
        }
    };

    const selectPatient = async (p) => {
        setPickerOpen(false);
        try {
            // Fetch the full patient record so the InvoiceFormDialog gets
            // the patient's cases (lets the doctor optionally link the
            // new invoice to a case).
            const r = await api.get(`/doctor/patients/${p.id}`);
            setPickedPatient({ id: r.data.id, name: r.data.name });
            setPickedPatientCases(r.data.cases || []);
            setFormOpen(true);
        } catch {
            // Fall back to opening the form without cases — better than blocking.
            setPickedPatient({ id: p.id, name: p.name });
            setPickedPatientCases([]);
            setFormOpen(true);
        }
    };

    useEffect(() => {
        api.get("/doctor/provider-invoices")
            .then((r) => {
                const items = r.data || [];
                setB2bCount(items.length);
                setB2bOutstanding(items.filter((i) => (i.total - i.amount_paid) > 0 && i.status !== "void" && i.status !== "cancelled").length);
            })
            .catch(() => {});
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/doctor/invoices");
            setInvoices(r.data || []);
        } catch {
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const switchTab = (tab) => {
        setTopTab(tab);
        const next = new URLSearchParams(searchParams);
        if (tab === "invisique") next.set("tab", "invisique"); else next.delete("tab");
        setSearchParams(next, { replace: true });
    };

    /* Summary stats — across ALL non-void invoices for this doctor. */
    const summary = useMemo(() => {
        const live = invoices.filter((i) => i.status !== "void");
        const unpaid = live.filter((i) => (i.balance || 0) > 0);
        const overdueList = live.filter(isOverdue);
        const paidThisMonth = live.reduce((sum, i) => {
            for (const p of (i.payments || [])) {
                if (isInCurrentMonth(p.paid_at)) sum += p.amount || 0;
            }
            return sum;
        }, 0);
        return {
            outstanding: live.reduce((s, i) => s + (i.balance || 0), 0),
            unpaidCount: unpaid.length,
            overdueCount: overdueList.length,
            overdueAmount: overdueList.reduce((s, i) => s + (i.balance || 0), 0),
            overdueList,
            paidThisMonth,
            billed: live.reduce((s, i) => s + (i.total || 0), 0),
        };
    }, [invoices]);

    const chipCounts = useMemo(() => {
        const counts = { all: invoices.length };
        for (const t of STATUS_CHIPS) {
            if (t.id === "all") continue;
            if (t.id === "overdue") {
                counts[t.id] = invoices.filter(isOverdue).length;
            } else if (t.id === "outstanding") {
                // iter100.194 — Same predicate the KPI card + filter use.
                counts[t.id] = invoices.filter(
                    (i) => (i.balance || 0) > 0 && i.status !== "void",
                ).length;
            } else if (t.id === "paid_this_month") {
                counts[t.id] = invoices.filter(
                    (i) => (i.payments || []).some((p) => isInCurrentMonth(p.paid_at)),
                ).length;
            } else {
                counts[t.id] = invoices.filter((i) => i.status === t.id).length;
            }
        }
        return counts;
    }, [invoices]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        // iter102.37 — Method filter: skip the check entirely when all
        // methods are selected (fast-path for the default state).
        const allMethodsOn = methodFilter.size === METHOD_KEYS.length;
        return invoices.filter((inv) => {
            // iter100.194 — Two synthetic filters wired from the KPI
            // strip clicks; not real invoice statuses so we branch on
            // them explicitly before falling through to the real
            // status match.
            if (statusChip === "outstanding") {
                if ((inv.balance || 0) <= 0) return false;
                if (inv.status === "void") return false;
            } else if (statusChip === "paid_this_month") {
                const hasPaymentThisMonth = (inv.payments || []).some(
                    (p) => isInCurrentMonth(p.paid_at),
                );
                if (!hasPaymentThisMonth) return false;
            } else if (statusChip === "overdue") {
                if (!isOverdue(inv)) return false;
            } else if (statusChip !== "all" && inv.status !== statusChip) {
                return false;
            }
            // Payment-method predicate — only invoices whose payment
            // ledger touches one of the selected methods survive.
            // Invoices with zero payments are dropped when a subset is
            // active, mirroring the export dialog's semantics.
            if (!allMethodsOn) {
                const payments = inv.payments || [];
                if (!payments.length) return false;
                const hit = payments.some((p) => methodFilter.has((p.method || "cash").toLowerCase()));
                if (!hit) return false;
            }
            if (!q) return true;
            return (
                (inv.code || "").toLowerCase().includes(q) ||
                (inv.patient_name || "").toLowerCase().includes(q) ||
                (inv.line_items || []).some((li) => (li.description || "").toLowerCase().includes(q))
            );
        });
    }, [invoices, statusChip, query, methodFilter]);

    const tableSummary = useMemo(() => {
        const billed = filtered.reduce((s, i) => s + (i.total || 0), 0);
        const collected = filtered.reduce((s, i) => s + ((i.total || 0) - (i.balance || 0)), 0);
        const outstanding = filtered.reduce((s, i) => s + (i.balance || 0), 0);
        return { billed, collected, outstanding };
    }, [filtered]);

    const openInvoice = invoices.find((i) => i.id === openInvoiceId);

    const invisiqueExportRef = useRef(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    // Raw un-filtered invoices used ONLY when the doctor overrides the
    // Hide-cash exclusion for a one-off "include cash" export. We keep
    // `invoices` (the unfiltered list) alongside `invoices` (the
    // discretion-aware list) so the dialog can toggle between them.
    const handleExport = () => {
        if (topTab === "invisique") {
            invisiqueExportRef.current?.();
            return;
        }
        setExportDialogOpen(true);
    };

    return (
        <div data-testid="doctor-billing-page" className="space-y-5">
            {/* ─── HEADER — matches /doctor/cases + /doctor/patients exactly ─── */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Billing</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        {topTab === "patient"
                            ? "Every invoice your clinic has issued to its patients, in one place."
                            : "Invoices Invisique™ HQ has issued to your clinic for manufacturing and shipping."}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={handleExport}
                        data-testid="billing-export-btn"
                        title={topTab === "invisique"
                            ? "Download the currently-filtered Invisique™ invoices as CSV"
                            : "Download the currently-filtered patient invoices as CSV"}
                    >
                        <DownloadSimple size={13} className="mr-1.5" /> Export
                    </Button>
                    {topTab === "patient" && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                            onClick={() => setShowInvoicePicker(true)}
                            data-testid="billing-record-payment-btn"
                            title="Record a payment against any open invoice"
                            disabled={
                                !invoices.some((i) => ["sent", "viewed", "partial"].includes(i.status) && (i.balance || 0) > 0)
                            }
                        >
                            <Money size={13} className="mr-1.5" /> Record payment
                        </Button>
                    )}
                    {topTab === "patient" && (
                        <Button
                            size="sm"
                            className="rounded-full"
                            onClick={openPatientPicker}
                            data-testid="billing-new-invoice-btn"
                            title="Pick a patient to invoice"
                        >
                            <Plus size={13} className="mr-1.5" /> New invoice
                        </Button>
                    )}
                </div>
            </div>

            {/* ─── SEGMENTED TAB BAR ─── */}
            <div
                className="inline-flex bg-card border border-border rounded-xl p-1 gap-0.5"
                role="tablist"
                data-testid="billing-toptabs"
            >
                <button
                    role="tab"
                    aria-selected={topTab === "patient"}
                    onClick={() => switchTab("patient")}
                    data-testid="billing-top-patient"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        topTab === "patient"
                            ? "bg-primary text-white shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`}
                >
                    <Receipt size={14} weight={topTab === "patient" ? "fill" : "duotone"} />
                    Patient Invoices
                    <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            topTab === "patient" ? "bg-white/25 text-white" : "bg-secondary text-muted-foreground"
                        }`}
                        data-testid="billing-top-patient-count"
                    >
                        {invoices.length}
                    </span>
                </button>
                <button
                    role="tab"
                    aria-selected={topTab === "invisique"}
                    onClick={() => switchTab("invisique")}
                    data-testid="billing-top-invisique"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        topTab === "invisique"
                            ? "bg-primary text-white shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`}
                >
                    <Buildings size={14} weight={topTab === "invisique" ? "fill" : "duotone"} />
                    Invisique™ Invoices
                    <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            b2bOutstanding > 0 && topTab !== "invisique"
                                ? "bg-rose-100 text-rose-700"
                                : topTab === "invisique"
                                    ? "bg-white/25 text-white"
                                    : "bg-secondary text-muted-foreground"
                        }`}
                        data-testid="billing-top-invisique-count"
                    >
                        {b2bCount}{b2bOutstanding > 0 ? ` · ${b2bOutstanding} due` : ""}
                    </span>
                </button>
            </div>

            {topTab === "invisique" ? (
                <InvisiqueInvoicesPanel openId={openInvoiceId} setOpenId={setOpenInvoiceId} exportRef={invisiqueExportRef} />
            ) : (
                <>
                    {/* ─── STAT STRIP — 5 cards, no left rails ─── */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="billing-summary">
                        <StatCard
                            label="Outstanding A/R"
                            value={formatINR(summary.outstanding)}
                            sub={`${summary.unpaidCount} unpaid invoice${summary.unpaidCount === 1 ? "" : "s"}`}
                            subTone={summary.unpaidCount > 0 ? "danger" : undefined}
                            tone={summary.outstanding > 0 ? "rose" : "neutral"}
                            icon={Warning}
                            testid="billing-stat-outstanding"
                            onClick={summary.unpaidCount > 0 ? () => setStatusChip("outstanding") : undefined}
                        />
                        <StatCard
                            label="Overdue"
                            value={formatINR(summary.overdueAmount)}
                            sub={`${summary.overdueCount} past due date`}
                            subTone={summary.overdueCount > 0 ? "warn" : undefined}
                            tone={summary.overdueCount > 0 ? "amber" : "neutral"}
                            icon={Clock}
                            testid="billing-stat-overdue"
                            onClick={summary.overdueCount > 0 ? () => setStatusChip("overdue") : undefined}
                        />
                        <StatCard
                            label="Collected This Month"
                            value={formatINR(summary.paidThisMonth)}
                            sub="From all patients"
                            subTone="success"
                            tone="emerald"
                            icon={TrendUp}
                            testid="billing-stat-collected"
                            onClick={summary.paidThisMonth > 0 ? () => setStatusChip("paid_this_month") : undefined}
                        />
                        <StatCard
                            label="Billed (Lifetime)"
                            value={formatINR(summary.billed)}
                            sub="Across all patients"
                            tone="sky"
                            icon={ChartLineUp}
                            testid="billing-stat-billed"
                        />
                        <StatCard
                            label="Total Invoices"
                            value={invoices.length.toLocaleString()}
                            sub="Incl. drafts & void"
                            tone="violet"
                            icon={Stack}
                            testid="billing-stat-total"
                        />
                    </div>

                    {/* ─── OVERDUE BANNER ─── */}
                    {summary.overdueCount > 0 && (
                        <OverdueBanner overdueList={summary.overdueList} onView={() => setStatusChip("overdue")} />
                    )}

                    {/* ─── CONTROLS BAR ─── iter100.197 · Shared
                        FilterSearchBar primitive.
                        iter102.38 — Payment-method filter moved into the
                        search-bar's rightSlot so the doctor has one
                        place to steer the visible slice. The old
                        "Remind all overdue" quick-CTA is retired — the
                        Overdue chip below already surfaces overdue rows
                        in one tap. */}
                    <FilterSearchBar
                        query={query}
                        setQuery={setQuery}
                        placeholder="Search patient, invoice #, item…"
                        searchTestid="billing-search"
                        testid="billing-controls-bar"
                        rightSlot={
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="billing-method-filter-trigger"
                                        className={`inline-flex items-center gap-2 text-xs font-medium px-3 h-9 rounded-full border whitespace-nowrap transition-colors ${
                                            methodFilter.size === METHOD_KEYS.length
                                                ? "bg-background border-border text-foreground hover:bg-secondary"
                                                : "bg-primary/10 border-primary/40 text-primary"
                                        }`}
                                    >
                                        <Wallet size={13} weight="duotone" />
                                        <span>
                                            {methodFilter.size === METHOD_KEYS.length
                                                ? "All methods"
                                                : methodFilter.size === 0
                                                    ? "No methods"
                                                    : `${methodFilter.size} method${methodFilter.size === 1 ? "" : "s"}`}
                                        </span>
                                        <CaretDown size={11} weight="bold" className="opacity-70" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    className="w-56 rounded-xl"
                                    data-testid="billing-method-filter-menu"
                                >
                                    <DropdownMenuLabel className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
                                        Payment method
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {METHOD_KEYS.map((m) => {
                                        const style = METHOD_PILL_STYLE[m];
                                        const checked = methodFilter.has(m);
                                        return (
                                            <DropdownMenuCheckboxItem
                                                key={m}
                                                checked={checked}
                                                onCheckedChange={() => {
                                                    setMethodFilter((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(m)) next.delete(m);
                                                        else next.add(m);
                                                        return next;
                                                    });
                                                }}
                                                onSelect={(e) => e.preventDefault()}
                                                data-testid={`billing-method-filter-${m}`}
                                                className="text-xs"
                                            >
                                                <span
                                                    className={`inline-block w-2 h-2 rounded-full mr-2 ${
                                                        m === "cash" ? "bg-amber-500"
                                                        : m === "upi" ? "bg-primary"
                                                        : m === "card" ? "bg-violet-500"
                                                        : "bg-slate-400"
                                                    }`}
                                                />
                                                {style.label}
                                            </DropdownMenuCheckboxItem>
                                        );
                                    })}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onSelect={(e) => {
                                            e.preventDefault();
                                            setMethodFilter(new Set(METHOD_KEYS));
                                        }}
                                        disabled={methodFilter.size === METHOD_KEYS.length}
                                        data-testid="billing-method-filter-reset"
                                        className="text-xs font-semibold text-primary justify-center focus:text-primary"
                                    >
                                        Reset — show all methods
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        }
                    />

                    {/* ─── STATUS CHIP ROW ─── matches /doctor/cases dot + count pill style */}
                    <div className="flex gap-1.5 flex-wrap items-center" data-testid="billing-status-chips">
                        {STATUS_CHIPS.map((t) => {
                            const active = statusChip === t.id;
                            const count = chipCounts[t.id] || 0;
                            const tone = t.id === "overdue"          ? "rose"
                                       : t.id === "outstanding"      ? "rose"
                                       : t.id === "partial"          ? "amber"
                                       : t.id === "paid"             ? "emerald"
                                       : t.id === "paid_this_month"  ? "emerald"
                                                                     : "primary";
                            const activeCls = active
                                ? (tone === "rose"    ? "bg-rose-100 border-rose-300 text-rose-700"
                                 : tone === "amber"   ? "bg-amber-100 border-amber-300 text-amber-800"
                                 : tone === "emerald" ? "bg-emerald-100 border-emerald-300 text-emerald-700"
                                                      : "bg-primary/10 border-primary/40 text-primary")
                                : "bg-background border-border text-muted-foreground hover:bg-secondary";
                            const dotCls = tone === "rose"    ? "bg-rose-500"
                                          : tone === "amber"   ? "bg-amber-500"
                                          : tone === "emerald" ? "bg-emerald-500"
                                                               : "bg-primary";
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setStatusChip(t.id)}
                                    data-testid={`billing-status-${t.id}`}
                                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors ${activeCls}`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
                                    {t.label}
                                    <span className="text-[9px] font-bold bg-black/5 px-1.5 py-0.5 rounded-full tabular-nums min-w-[2rem] inline-block text-center">
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* ─── TABLE ─── */}
                    <div className="bg-card border border-border rounded-2xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="billing-table">
                                <thead className="bg-secondary/50 border-b border-border">
                                    <tr className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                        <th className="text-left px-4 py-2.5">Patient</th>
                                        <th className="text-left px-4 py-2.5">Invoice #</th>
                                        <th className="text-left px-4 py-2.5 whitespace-nowrap">Issued</th>
                                        <th className="text-left px-4 py-2.5">Amount</th>
                                        <th className="text-left px-4 py-2.5 whitespace-nowrap">Balance Due</th>
                                        <th className="text-left px-4 py-2.5">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border/60">
                                    {loading ? (
                                        <tr><td colSpan={6} className="p-12 text-center text-xs text-muted-foreground">Loading invoices…</td></tr>
                                    ) : filtered.length === 0 ? (
                                        <tr><td colSpan={6}><EmptyState query={query} statusChip={statusChip} methodFilter={methodFilter} /></td></tr>
                                    ) : (
                                        filtered.map((inv) => (
                                            <InvoiceTableRow
                                                key={inv.id}
                                                invoice={inv}
                                                onOpen={() => setOpenInvoiceId(inv.id)}
                                                onQuickPay={() => setQuickPayInvoice(inv)}
                                            />
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Table footer summary */}
                        {!loading && filtered.length > 0 && (
                            <div className="px-4 py-3 border-t border-border bg-secondary/30 flex items-center justify-between gap-3 flex-wrap text-xs" data-testid="billing-table-footer">
                                <div className="text-muted-foreground">
                                    Showing 1–{filtered.length} of {filtered.length} invoice{filtered.length === 1 ? "" : "s"}
                                </div>
                                <div className="flex items-center gap-4 flex-wrap">
                                    <span className="text-muted-foreground">
                                        Billed: <strong className="text-foreground tabular-nums">{formatINR(tableSummary.billed)}</strong>
                                    </span>
                                    <span className="text-muted-foreground">
                                        Collected: <strong className="text-emerald-700 tabular-nums">{formatINR(tableSummary.collected)}</strong>
                                    </span>
                                    <span className="text-muted-foreground">
                                        Outstanding: <strong className="text-rose-700 tabular-nums">{formatINR(tableSummary.outstanding)}</strong>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            <InvoiceDetailDialog
                open={topTab === "patient" && !!openInvoiceId}
                invoice={openInvoice}
                onClose={() => setOpenInvoiceId(null)}
                onChanged={load}
            />

            {/* iter98.99z — top-level Record-Payment CTA → invoice picker → quick pay */}
            <PickInvoiceForPaymentDialog
                open={showInvoicePicker}
                invoices={invoices}
                onClose={() => setShowInvoicePicker(false)}
                onPick={(inv) => { setShowInvoicePicker(false); setQuickPayInvoice(inv); }}
            />

            {/* iter102.36 — Export dialog. Opens when the doctor clicks
                Export on the Patient tab. Receives the raw invoice list
                filtered only by the on-screen status chip + search box;
                the dialog's Payment Methods chip row is now the sole
                filter for cash inclusion. Hide-cash on-screen no longer
                influences exports (decoupled 2026-08-25). */}
            <BillingExportDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                invoices={invoices.filter((i) => {
                    // Mirror the parent's status/search filter on the raw
                    // (non-discretion-aware) list. The dialog's Cash chip
                    // is now the sole source of truth for cash inclusion —
                    // Hide-cash on-screen no longer influences the export.
                    const q = query.trim().toLowerCase();
                    if (statusChip === "outstanding") {
                        if ((i.balance || 0) <= 0 || i.status === "void") return false;
                    } else if (statusChip === "overdue") {
                        if (!isOverdue(i)) return false;
                    } else if (statusChip === "paid_this_month") {
                        if (!(i.payments || []).some((p) => isInCurrentMonth(p.paid_at))) return false;
                    } else if (statusChip !== "all" && i.status !== statusChip) {
                        return false;
                    }
                    if (!q) return true;
                    return (
                        (i.code || "").toLowerCase().includes(q) ||
                        (i.patient_name || "").toLowerCase().includes(q) ||
                        (i.line_items || []).some((li) => (li.description || "").toLowerCase().includes(q))
                    );
                })}
                statusChipLabel={
                    statusChip === "all" ? null
                    : (STATUS_CHIPS.find((c) => c.id === statusChip)?.label || statusChip)
                }
                searchQuery={query.trim() || null}
            />
            {/* iter98.99z — Standalone quick-pay dialog (used by both the
                top-level CTA and per-row Pay buttons). */}
            <RecordPaymentDialog
                open={!!quickPayInvoice}
                invoice={quickPayInvoice}
                onClose={() => setQuickPayInvoice(null)}
                onSaved={() => { setQuickPayInvoice(null); load(); }}
            />

            {/* iter102.09 — Patient picker premium redesign.
                Shown when the doctor clicks "New invoice". Selecting a
                row opens InvoiceFormDialog scoped to that patient — no
                page navigation. Compact, laptop-friendly layout with a
                sectioned header + editorial teal wash matching the
                Appointments dialogs. */}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="billing-new-invoice-picker">
                    {/* ── Header band ── */}
                    <div className="relative px-5 pt-4 pb-3 border-b border-border bg-gradient-to-br from-primary/[0.06] via-primary/[0.02] to-transparent">
                        <div className="flex items-start gap-2.5">
                            <div className="w-9 h-9 rounded-lg bg-primary/12 grid place-items-center shrink-0">
                                <Receipt size={17} weight="duotone" className="text-primary" />
                            </div>
                            <DialogHeader className="flex-1 min-w-0 text-left space-y-0.5">
                                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary/80">
                                    Billing · New invoice
                                </div>
                                <DialogTitle className="text-[17px] font-bold leading-tight">
                                    Pick a patient to invoice
                                </DialogTitle>
                                <DialogDescription className="text-[11.5px] text-muted-foreground leading-snug">
                                    Search by name or phone — we&apos;ll open the invoice form scoped to whoever you pick.
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                    </div>

                    {/* ── Search field ── */}
                    <div className="px-5 pt-3 pb-2">
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={pickerQuery}
                                onChange={(e) => setPickerQuery(e.target.value)}
                                placeholder="Search by name or phone…"
                                className="pl-9 h-9 text-[13px]"
                                autoFocus
                                data-testid="billing-new-invoice-picker-search"
                            />
                            {pickerQuery && (
                                <button
                                    type="button"
                                    onClick={() => setPickerQuery("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-md hover:bg-secondary text-muted-foreground"
                                    data-testid="billing-new-invoice-picker-clear"
                                    aria-label="Clear search"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Results list ── */}
                    <div className="max-h-[52vh] overflow-y-auto px-3 pb-3" data-testid="billing-new-invoice-picker-list">
                        {pickerLoading && (
                            <div className="text-[11.5px] text-muted-foreground text-center py-6">Loading patients…</div>
                        )}
                        {(() => {
                            const q = pickerQuery.trim().toLowerCase();
                            const filtered = !pickerLoading
                                ? pickerPatients.filter((p) => {
                                    if (!q) return true;
                                    return (p.name || "").toLowerCase().includes(q)
                                        || (p.phone || "").toLowerCase().includes(q);
                                }).slice(0, 50)
                                : [];
                            const showCreate = !pickerLoading && filtered.length === 0;
                            const total = pickerPatients.length;
                            return (
                                <>
                                    {!pickerLoading && filtered.length > 0 && (
                                        <div className="px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                                            {q
                                                ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}`
                                                : `Recent patients · ${filtered.length} of ${total}`}
                                        </div>
                                    )}
                                    <div className="space-y-0.5">
                                        {filtered.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => selectPatient(p)}
                                                className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-primary/[0.06] focus:bg-primary/[0.08] outline-none text-left transition-colors"
                                                data-testid={`billing-new-invoice-picker-row-${p.id}`}
                                            >
                                                <PatientAvatar name={p.name} size={34} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-[13px] font-semibold truncate text-foreground group-hover:text-primary transition-colors">
                                                        {p.name || "Unnamed patient"}
                                                    </div>
                                                    {p.phone && (
                                                        <div className="text-[11px] text-muted-foreground truncate font-mono">
                                                            {p.phone}
                                                        </div>
                                                    )}
                                                </div>
                                                <ArrowRight
                                                    size={14}
                                                    weight="bold"
                                                    className="text-muted-foreground/60 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                    {showCreate && (
                                        <div className="px-3 py-6 flex flex-col items-center text-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-primary/10 grid place-items-center">
                                                <MagnifyingGlass size={20} weight="duotone" className="text-primary/70" />
                                            </div>
                                            <div className="space-y-0.5">
                                                <div className="text-[13px] font-semibold text-foreground">
                                                    {pickerPatients.length === 0
                                                        ? "No patients on file yet"
                                                        : q
                                                            ? `No matches for “${pickerQuery.trim()}”`
                                                            : "Nothing to show"}
                                                </div>
                                                <div className="text-[11.5px] text-muted-foreground max-w-[260px]">
                                                    {pickerPatients.length === 0
                                                        ? "Add your first patient to get started with billing."
                                                        : "Try a different spelling — or add them as a new record."}
                                                </div>
                                            </div>
                                            <Button
                                                type="button"
                                                size="sm"
                                                className="rounded-full h-9 px-4 bg-primary hover:bg-primary/90 text-white text-[12.5px] font-semibold shadow-sm hover:shadow-md transition-shadow"
                                                onClick={() => {
                                                    setAddPatientPrefill(pickerQuery.trim());
                                                    setPickerOpen(false);
                                                    setShowAddPatient(true);
                                                }}
                                                data-testid="billing-new-invoice-picker-create"
                                            >
                                                <Plus size={13} weight="bold" className="mr-1.5" />
                                                {q ? `Create “${pickerQuery.trim()}”` : "Add new patient"}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </DialogContent>
            </Dialog>

            {pickedPatient && (
                <InvoiceFormDialog
                    open={formOpen}
                    onClose={() => setFormOpen(false)}
                    onSaved={() => { setFormOpen(false); load(); }}
                    patientId={pickedPatient.id}
                    patientName={pickedPatient.name}
                    cases={pickedPatientCases}
                />
            )}

            {/* iter98.99y — Inline patient creation triggered from the
                picker. On create, we splice the new patient into the
                cached list and immediately open the invoice form for them. */}
            <AddPatientDialog
                open={showAddPatient}
                onOpenChange={setShowAddPatient}
                defaultName={addPatientPrefill}
                onCreated={(created) => {
                    setPickerPatients((prev) => [created, ...prev]);
                    selectPatient(created);
                    setShowAddPatient(false);
                    setAddPatientPrefill("");
                }}
            />
        </div>
    );
}


/* ─────────────── Overdue banner ─────────────── */
function OverdueBanner({ overdueList, onView }) {
    const top2 = overdueList.slice(0, 2);
    const summary = top2.map((i) => `${i.patient_name || "—"} (${formatINR(i.balance)})`).join(" and ");
    const extra = overdueList.length > 2 ? ` and ${overdueList.length - 2} more` : "";
    return (
        <div
            className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-start gap-3"
            data-testid="billing-overdue-banner"
        >
            <Warning size={18} weight="fill" className="text-rose-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-sm text-rose-900">
                <strong className="font-semibold">
                    {overdueList.length} invoice{overdueList.length === 1 ? " is" : "s are"} overdue.
                </strong>{" "}
                <span className="text-rose-800">{summary}{extra}.</span>
            </div>
            <button
                type="button"
                onClick={onView}
                className="text-xs font-semibold text-rose-700 underline hover:text-rose-900 whitespace-nowrap shrink-0 inline-flex items-center gap-1"
                data-testid="billing-overdue-view"
            >
                <PaperPlaneTilt size={11} weight="fill" /> View overdue
            </button>
        </div>
    );
}


/* ─────────────── Table row ─────────────── */
function InvoiceTableRow({ invoice, onOpen, onQuickPay }) {
    const overdue = isOverdue(invoice);
    const status = overdue ? "overdue" : invoice.status;
    const tone = STATUS_TONE[status] || STATUS_TONE.draft;
    const paid = (invoice.total || 0) - (invoice.balance || 0);

    /* iter102.33 — Payment-method mix for this invoice. We aggregate
       amounts by method so we can (a) render one pill per distinct
       method, (b) show the exact amount as a tooltip on hover. */
    const methodBreakdown = React.useMemo(() => {
        const map = new Map();
        for (const p of (invoice.payments || [])) {
            const m = (p.method || "cash").toString().toLowerCase();
            map.set(m, (map.get(m) || 0) + Number(p.amount || 0));
        }
        // Sort by amount descending so the dominant method shows first.
        return Array.from(map.entries())
            .map(([method, amt]) => ({ method, amt }))
            .sort((a, b) => b.amt - a.amt);
    }, [invoice.payments]);
    return (
        <tr
            className="hover:bg-secondary/30 cursor-pointer transition-colors"
            onClick={onOpen}
            data-testid={`billing-row-${invoice.id}`}
        >
            <td className="px-4 py-3 align-middle">
                <div className="flex items-center gap-2.5 min-w-0">
                    <PatientAvatar
                        patient={{
                            id: invoice.patient_id,
                            name: invoice.patient_name,
                            photo_data_url: invoice.patient_photo_data_url,
                        }}
                        size="sm"
                    />
                    <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{invoice.patient_name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                            {invoice.case_code || "—"}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 align-middle">
                <span className="font-mono text-xs font-semibold bg-secondary/60 border border-border rounded-md px-2 py-0.5 inline-block">
                    {invoice.code}
                </span>
            </td>
            <td className="px-4 py-3 align-middle text-xs text-muted-foreground whitespace-nowrap">
                {invoice.issued_at ? fmtDateShort(invoice.issued_at) : "—"}
            </td>
            <td className="px-4 py-3 align-middle">
                <div className="text-sm font-bold tabular-nums">{formatINR(invoice.total)}</div>
                {paid > 0 && (
                    <div className="text-[10px] text-muted-foreground tabular-nums">Paid: {formatINR(paid)}</div>
                )}
                {/* iter102.33 — Method pill row. Kept in the Amount cell so
                    it inherits its narrow width and doesn't disturb the
                    other columns. Multiple methods stack horizontally
                    with a 3px gap; two-method rows fit on one line
                    (e.g. UPI + CARD) without wrapping the total. */}
                {methodBreakdown.length > 0 && (
                    <div
                        className="mt-1 flex items-center gap-1 flex-wrap"
                        data-testid={`billing-row-methods-${invoice.id}`}
                    >
                        {methodBreakdown.map(({ method, amt }) => {
                            const p = METHOD_PILL_STYLE[method] || METHOD_PILL_STYLE.other;
                            return (
                                <span
                                    key={method}
                                    className={`inline-flex items-center text-[9px] font-bold uppercase tracking-[0.06em] px-1.5 py-[1px] rounded border ${p.className}`}
                                    title={`${p.label}: ${formatINR(amt)}`}
                                    data-testid={`billing-row-method-${method}-${invoice.id}`}
                                >
                                    {p.label}
                                </span>
                            );
                        })}
                    </div>
                )}
                {(invoice.credits_applied || 0) > 0 && (
                    <div
                        className="text-[10px] text-emerald-700 font-semibold tabular-nums"
                        title="Applied from your referral credit"
                        data-testid={`billing-row-credits-${invoice.id}`}
                    >
                        Credit: -{formatINR(invoice.credits_applied)}
                    </div>
                )}
            </td>
            <td className="px-4 py-3 align-middle">
                <div className="text-sm font-semibold tabular-nums">{formatINR(invoice.balance)}</div>
                {invoice.due_at && (
                    <div className={`text-[10px] tabular-nums ${overdue ? "text-rose-700 font-medium" : "text-muted-foreground"}`}>
                        Due: {fmtDateShort(invoice.due_at)}
                    </div>
                )}
            </td>
            <td className="px-4 py-3 align-middle">
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${tone.bg}`}>
                    {overdue && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />}
                    {tone.label}
                </span>
            </td>
        </tr>
    );
}

function RowAction({ children, title, onClick, testid, tone }) {
    const cls = tone === "primary"
        ? "border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/40"
        : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground";
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            data-testid={testid}
            className={`w-7 h-7 rounded-md border grid place-items-center transition-colors ${cls}`}
        >
            {children}
        </button>
    );
}


/* ─────────────── Empty state ─────────────── */
function EmptyState({ query, statusChip, methodFilter }) {
    const methodFilterActive = methodFilter && methodFilter.size !== METHOD_KEYS.length;
    const isFiltered = query || statusChip !== "all" || methodFilterActive;
    if (isFiltered) {
        return (
            <div className="p-12 text-center" data-testid="billing-empty-filtered">
                <FunnelSimple size={20} weight="duotone" className="mx-auto text-muted-foreground/60 mb-3" />
                <p className="text-sm text-foreground font-medium">No invoices match your filter</p>
                <p className="text-xs text-muted-foreground mt-1">
                    Try clearing the search{methodFilterActive ? ", resetting the payment-method filter," : ""} or switching the status chip.
                </p>
            </div>
        );
    }
    return (
        <div className="p-12 text-center" data-testid="billing-empty-all">
            <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                <CurrencyInr size={20} weight="duotone" className="text-muted-foreground" />
            </div>
            <p className="text-sm text-foreground font-medium">No invoices yet</p>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
                Use the &quot;New invoice&quot; button above — pick a patient and create the first invoice without leaving this page.
            </p>
            <Button asChild size="sm" variant="outline" className="rounded-full">
                <Link to="/doctor/patients">Browse patients <ArrowRight size={12} className="ml-1" /></Link>
            </Button>
        </div>
    );
}


/* ─────────────────── Invisique™ invoices panel (B2B billing) ─────────────────── */
function InvisiqueInvoicesPanel({ openId, setOpenId, exportRef }) {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    /* iter100.194 — Panel-scoped status filter. Two synthetic values
       ("outstanding", "paid_this_month") are wired to the top KPI
       cards' clicks; "all" is the default. No dedicated chip row
       below the search — the two card clicks are the only entry
       points, and the Search bar's clear + the KPI card re-click
       toggle back to "all". */
    const [statusFilter, setStatusFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/doctor/provider-invoices");
            setInvoices(r.data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const summary = useMemo(() => {
        // iter98.99e0 — terminal/non-AR statuses excluded from outstanding totals.
        const SKIP = new Set(["void", "cancelled"]);
        let outstanding = 0, unpaidCount = 0, paidThisMonth = 0, billed = 0;
        for (const inv of invoices) {
            if (SKIP.has(inv.status)) continue;
            billed += inv.total || 0;
            const balance = (inv.total || 0) - (inv.amount_paid || 0);
            if (balance > 0 && inv.status !== "draft") {
                outstanding += balance;
                unpaidCount += 1;
            }
            if (isInCurrentMonth(inv.paid_at)) paidThisMonth += inv.amount_paid || 0;
        }
        return { outstanding, unpaidCount, paidThisMonth, billed };
    }, [invoices]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return invoices.filter((inv) => {
            // iter100.194 — Synthetic filters wired to the KPI card clicks.
            if (statusFilter === "outstanding") {
                const balance = (inv.total || 0) - (inv.amount_paid || 0);
                if (balance <= 0) return false;
                if (inv.status === "void" || inv.status === "cancelled" || inv.status === "draft") return false;
            } else if (statusFilter === "paid_this_month") {
                if (!isInCurrentMonth(inv.paid_at)) return false;
            }
            if (!q) return true;
            return (
                (inv.code || "").toLowerCase().includes(q) ||
                (inv.case_type || "").toLowerCase().includes(q) ||
                (inv.patient_name || "").toLowerCase().includes(q) ||
                (inv.line_items || []).some((li) => (li.description || "").toLowerCase().includes(q))
            );
        });
    }, [invoices, query, statusFilter]);

    /* iter100.194 — Expose a CSV-export callback to the parent via
       `exportRef` so the shared header Export button can trigger this
       panel's export without hoisting the invoice list. Callback reads
       from `filtered` closure, always current. */
    useEffect(() => {
        if (!exportRef) return;
        exportRef.current = () => {
            const rows = filtered.map((inv) => ({
                "Invoice #":  inv.code || inv.id,
                "Case":       inv.case_type || "",
                "Patient":    inv.patient_name || "",
                "Issued":     inv.issued_at ? new Date(inv.issued_at).toISOString().slice(0, 10) : "",
                "Status":     inv.status || "",
                "Total":      inv.total || 0,
                "Paid":       inv.amount_paid || 0,
                "Balance":    (inv.total || 0) - (inv.amount_paid || 0),
            }));
            downloadCsv(rows, `invisique-invoices-${todayStamp()}.csv`);
        };
        return () => {
            if (exportRef.current) exportRef.current = null;
        };
    }, [filtered, exportRef]);

    const openInvoice = invoices.find((i) => i.id === openId);

    return (
        <div className="space-y-5" data-testid="invisique-invoices-panel">
            {/* 4-card stat strip — NO left rails */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                    label="Total Billed by Invisique™"
                    value={formatINR(summary.billed)}
                    sub="Lifetime manufacturing costs"
                    tone="violet"
                    icon={Buildings}
                    testid="invisique-stat-billed"
                />
                <StatCard
                    label="Unpaid to Invisique™"
                    value={formatINR(summary.outstanding)}
                    sub={`${summary.unpaidCount} invoice${summary.unpaidCount === 1 ? "" : "s"} due`}
                    subTone={summary.unpaidCount > 0 ? "danger" : undefined}
                    tone={summary.outstanding > 0 ? "rose" : "neutral"}
                    icon={Warning}
                    testid="invisique-stat-outstanding"
                    onClick={summary.unpaidCount > 0
                        ? () => setStatusFilter((f) => f === "outstanding" ? "all" : "outstanding")
                        : undefined}
                />
                <StatCard
                    label="Paid This Month"
                    value={formatINR(summary.paidThisMonth)}
                    sub="Payments cleared to HQ"
                    subTone="success"
                    tone="emerald"
                    icon={TrendUp}
                    testid="invisique-stat-paid"
                    onClick={summary.paidThisMonth > 0
                        ? () => setStatusFilter((f) => f === "paid_this_month" ? "all" : "paid_this_month")
                        : undefined}
                />
                <StatCard
                    label="Total Invoices"
                    value={invoices.length.toLocaleString()}
                    sub="Aligner & shipping orders"
                    tone="sky"
                    icon={Stack}
                    testid="invisique-stat-total"
                />
            </div>

            {/* Controls · iter100.197 uses the shared FilterSearchBar
                primitive so the Invisique tab has pixel-identical
                dimensions to every other list surface in the portal. */}
            <FilterSearchBar
                query={query}
                setQuery={setQuery}
                placeholder="Search invoice #, case, patient…"
                searchTestid="invisique-invoices-search"
            />

            {/* iter100.195 · Status filter chips — mirror the Patient tab
                so KPI-card clicks ("Unpaid to Invisique™" / "Paid This
                Month") surface their filter state where the doctor
                expects it, inline with the invoice list. */}
            <FilterChipRow
                value={statusFilter}
                onChange={setStatusFilter}
                testid="invisique-status-chips"
                chipTestidPrefix="invisique-chip"
                chips={[
                    { id: "all",             label: "All",             count: invoices.length,                                                                                                                          tone: "primary" },
                    { id: "outstanding",     label: "Unpaid",          count: invoices.filter((i) => ((i.total || 0) - (i.amount_paid || 0)) > 0 && !["void","cancelled","draft"].includes(i.status)).length,           tone: "rose"    },
                    { id: "paid_this_month", label: "Paid this month", count: invoices.filter((i) => isInCurrentMonth(i.paid_at)).length,                                                                                tone: "emerald" },
                ]}
            />

            {/* Table */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="invisique-table">
                        <thead className="bg-secondary/50 border-b border-border">
                            <tr className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                <th className="text-left px-4 py-2.5">Invoice #</th>
                                <th className="text-left px-4 py-2.5">Case / Patient</th>
                                <th className="text-left px-4 py-2.5">Description</th>
                                <th className="text-left px-4 py-2.5 whitespace-nowrap">Issued</th>
                                <th className="text-left px-4 py-2.5">Amount</th>
                                <th className="text-left px-4 py-2.5">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/60">
                            {loading ? (
                                <tr><td colSpan={6} className="p-12 text-center text-xs text-muted-foreground">Loading invoices…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={6}>
                                        <div className="p-12 text-center">
                                            <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                                                <Receipt size={20} weight="duotone" className="text-muted-foreground" />
                                            </div>
                                            <p className="text-sm text-foreground font-medium">No B2B invoices yet</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Invisique™ HQ raises an invoice when you approve a Center-of-Excellence treatment plan. You&apos;ll have 30 days to clear it.
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((inv) => (
                                    <ProviderInvoiceTableRow key={inv.id} invoice={inv} onOpen={() => setOpenId(inv.id)} />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ProviderInvoiceDialog
                invoice={openInvoice}
                onClose={() => setOpenId(null)}
            />
        </div>
    );
}

function ProviderInvoiceTableRow({ invoice, onOpen }) {
    // iter98.99e0 — backend now returns outstanding/partially_paid/overdue/paid/cancelled.
    // Fall back to `outstanding` (not the deprecated `sent`) for unknown statuses.
    const tone = STATUS_TONE[invoice.status] || STATUS_TONE.outstanding;
    const balance = (invoice.total || 0) - (invoice.amount_paid || 0);
    const label = tone.label;
    return (
        <tr
            className="hover:bg-secondary/30 cursor-pointer transition-colors"
            onClick={onOpen}
            data-testid={`provider-inv-row-${invoice.id}`}
        >
            <td className="px-4 py-3 align-middle">
                <span className="font-mono text-xs font-semibold bg-secondary/60 border border-border rounded-md px-2 py-0.5 inline-block">
                    {invoice.code}
                </span>
            </td>
            <td className="px-4 py-3 align-middle">
                <div className="text-xs text-foreground/80">
                    {(invoice.case_type || "—").replace(/_/g, " ")}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                    {invoice.patient_name || "—"}
                </div>
            </td>
            <td className="px-4 py-3 align-middle text-xs text-muted-foreground truncate max-w-[280px]">
                {(invoice.line_items || []).slice(0, 2).map((li) => li.description).join(" · ") || "—"}
            </td>
            <td className="px-4 py-3 align-middle text-xs text-muted-foreground whitespace-nowrap">
                {invoice.issued_at ? fmtDateShort(invoice.issued_at) : "—"}
            </td>
            <td className="px-4 py-3 align-middle">
                <div className="text-sm font-bold tabular-nums">{formatINR(invoice.total)}</div>
                {balance > 0 && status !== "draft" && (
                    <div className="text-[10px] text-rose-700 font-medium tabular-nums">{formatINR(balance)} due</div>
                )}
            </td>
            <td className="px-4 py-3 align-middle">
                <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${tone.bg}`}>
                    {label}
                </span>
            </td>
        </tr>
    );
}

/* Read-only modal for the doctor's view of a B2B invoice. */
function ProviderInvoiceDialog({ invoice, onClose }) {
    if (!invoice) return null;
    const tone = STATUS_TONE[invoice.status] || STATUS_TONE.sent;
    const balance = (invoice.total || 0) - (invoice.amount_paid || 0);
    return (
        <div
            className="fixed inset-0 bg-foreground/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="provider-invoice-dialog"
        >
            <div
                className="bg-card border border-border rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1">Invisique™ invoice</div>
                        <div className="font-mono text-lg font-bold">{invoice.code}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full border px-2 py-1 ${tone.bg}`}>
                        {tone.label}
                    </span>
                </div>
                <div className="px-6 py-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                            <div className="text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">Case</div>
                            <div className="text-foreground">{(invoice.case_type || "—").replace(/_/g, " ")}</div>
                        </div>
                        <div>
                            <div className="text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">Patient</div>
                            <div className="text-foreground">{invoice.patient_name || "—"}</div>
                        </div>
                        <div>
                            <div className="text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">Issued</div>
                            <div className="text-foreground">{invoice.issued_at ? fmtDateShort(invoice.issued_at) : "—"}</div>
                        </div>
                        <div>
                            <div className="text-muted-foreground uppercase tracking-wider font-semibold mb-0.5">Due</div>
                            <div className="text-foreground">{invoice.due_at ? fmtDateShort(invoice.due_at) : "—"}</div>
                        </div>
                    </div>

                    <div className="border-t border-border pt-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Line items</div>
                        <div className="space-y-1.5">
                            {(invoice.line_items || []).map((li, idx) => (
                                <div key={idx} className="flex justify-between text-sm gap-3">
                                    <div className="text-foreground">{li.description} <span className="text-muted-foreground">× {li.quantity}</span></div>
                                    <div className="text-foreground font-medium tabular-nums">{formatINR(li.line_total)}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-border pt-3 space-y-1.5 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="tabular-nums">{formatINR(invoice.subtotal)}</span>
                        </div>
                        {invoice.tax_amount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Tax ({invoice.tax_pct}%)</span>
                                <span className="tabular-nums">{formatINR(invoice.tax_amount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between font-bold pt-1.5 border-t border-border">
                            <span>Total</span>
                            <span className="tabular-nums">{formatINR(invoice.total)}</span>
                        </div>
                        {(invoice.credits_applied || 0) > 0 && (
                            <div
                                className="flex justify-between text-emerald-700 font-semibold"
                                data-testid="invoice-detail-credits-applied"
                                title="Applied from your referral rewards balance"
                            >
                                <span>Referral credit</span>
                                <span className="tabular-nums">-{formatINR(invoice.credits_applied)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-emerald-700">
                            <span>Paid</span>
                            <span className="tabular-nums">{formatINR(invoice.amount_paid)}</span>
                        </div>
                        <div className={`flex justify-between font-bold ${balance > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            <span>Balance</span>
                            <span className="tabular-nums">{formatINR(balance)}</span>
                        </div>
                    </div>

                    {(invoice.payments || []).length > 0 && (
                        <div className="border-t border-border pt-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Payments recorded</div>
                            <div className="space-y-1.5">
                                {(invoice.payments || []).map((p) => (
                                    <div key={p.id} className="flex justify-between text-xs">
                                        <div>
                                            <span className="font-mono">{p.paid_at ? fmtDateShort(p.paid_at) : "—"}</span>
                                            <span className="text-muted-foreground ml-2">{(p.method || "").replace(/_/g, " ")}</span>
                                            {p.reference && <span className="text-muted-foreground ml-2 font-mono">{p.reference}</span>}
                                        </div>
                                        <span className="font-semibold tabular-nums">{formatINR(p.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {balance > 0 && invoice.status !== "draft" && (
                        <div className="border-t border-border pt-3">
                            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-900">
                                <strong>Payment instructions:</strong> Settle this invoice via UPI / bank transfer to the account on file with Invisique™ HQ. Once paid, Invisique™ admin will mark this invoice as paid — you don&apos;t need to do anything in the portal.
                            </div>
                        </div>
                    )}

                    {invoice.notes && (
                        <div className="border-t border-border pt-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Notes</div>
                            <div className="text-sm text-foreground italic">{invoice.notes}</div>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-border flex justify-end">
                    <Button onClick={onClose} variant="outline" className="rounded-full" data-testid="provider-invoice-close">Close</Button>
                </div>
            </div>
        </div>
    );
}
